'use client';
import { createContext, useContext, useEffect, useId, useMemo, useRef, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { IconButton } from '@chakra-ui/react';
import { IconWaveSine } from '@tabler/icons-react';
import { isVoiceEnabledClient, type VoicePageContext } from '@/lib/voice/shared';

export type PageRegistration = Pick<VoicePageContext, 'title' | 'summary' | 'filters' | 'loading'>;
export type Phase = 'off' | 'connecting' | 'listening' | 'ending';
type VoiceContextValue = {
  phase: Phase;
  eligible: boolean;
  open: () => void;
  register: (key: string, value: PageRegistration | null) => void;
  registerEditors: (value: { canvas: boolean; designSystem: boolean; website: boolean }) => void;
  registerConversation: (
    key: string,
    value: { sessionId: string; previewIds: string[] } | null
  ) => void;
};
export const VoiceContext = createContext<VoiceContextValue | null>(null);

/** Caption and operation updates must not invalidate every registered page. */
export function VoiceContextProvider({
  phase,
  eligible,
  setVisible,
  start,
  register,
  registerEditors,
  registerConversation,
  children,
}: Omit<VoiceContextValue, 'open'> & {
  setVisible: (visible: boolean) => void;
  start?: () => void | Promise<void>;
  children: ReactNode;
}) {
  // The session starter reads current provider state but is not page context.
  const startRef = useRef(start);
  startRef.current = start;
  const value = useMemo(
    () => ({
      phase,
      eligible,
      open: () => {
        if (eligible || phase !== 'off') {
          setVisible(true);
          if (phase === 'off') void startRef.current?.();
        }
      },
      register,
      registerEditors,
      registerConversation,
    }),
    [phase, eligible, setVisible, register, registerEditors, registerConversation]
  );
  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
}

export function useVoicePage(value: PageRegistration) {
  const register = useContext(VoiceContext)?.register;
  const path = usePathname();
  const serialized = JSON.stringify(value);
  useEffect(() => {
    register?.(path, JSON.parse(serialized));
    return () => register?.(path, null);
  }, [path, register, serialized]);
}
/** Publish the same capabilities the layout uses to render editors. */
export function useVoiceEditors(canvas: boolean, designSystem: boolean, website: boolean) {
  const register = useContext(VoiceContext)?.registerEditors;
  useEffect(() => {
    register?.({ canvas, designSystem, website });
    return () => register?.({ canvas: false, designSystem: false, website: false });
  }, [register, canvas, designSystem, website]);
}
/** Register content that actually finished loading, independently of the requested URL. */
export function useVoiceLoadedConversation(sessionId: string | null, previewIds: string[]) {
  const register = useContext(VoiceContext)?.registerConversation;
  const key = useId();
  const serialized = JSON.stringify(previewIds);
  useEffect(() => {
    register?.(key, sessionId ? { sessionId, previewIds: JSON.parse(serialized) } : null);
    return () => register?.(key, null);
  }, [register, key, sessionId, serialized]);
}
export function VoiceControl({ compact = false }: { compact?: boolean }) {
  const voice = useContext(VoiceContext);
  if (!voice || !isVoiceEnabledClient() || (!voice.eligible && voice.phase === 'off')) return null;
  return (
    <IconButton
      aria-label={voice.phase === 'off' ? 'Talk to Attune' : 'Open voice controls'}
      title="Talk to Attune"
      size={compact ? 'sm' : 'md'}
      variant="ghost"
      borderRadius="full"
      color={voice.phase === 'off' ? 'ui.text.secondary' : 'accent.solid'}
      onClick={voice.open}
    >
      <IconWaveSine size={20} />
    </IconButton>
  );
}
